import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DetailsTabComponent } from './details-tab/details-tab.component';
import { LocalizationTabComponent } from './localization-tab/localization-tab.component';
import { DetailsPageHeaderComponent } from '@app/ui-components/details-page-header/details-page-header.component';
import {
  DatabaseManagement,
  EntityKey,
  Language,
} from '@app/models/admin.model';
import {
  ActiveLanguagesForm,
  DatabaseManagementFormGroup,
  MergeLanguagesWithoutType,
} from '@app/models/form.models';
import {
  FormGroup,
  FormArray,
  FormBuilder,
  Validators,
  FormControl,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GenericDataService } from '@app/services/generic-data.service';
import { MasterdataService } from '@app/services/admin/masterdata.service';
import { ToastService } from '@app/services/toast.service';
import { finalize, forkJoin } from 'rxjs';
import { DatabaseSaveService } from '@app/services/admin/database/database-save.service';

type Tab = 'details' | 'localizations';
interface TabDef {
  key: Tab;
  label: string;
}

@Component({
  selector: 'app-country-details',
  imports: [
    CommonModule,
    DetailsTabComponent,
    LocalizationTabComponent,
    DetailsPageHeaderComponent,
    TranslatePipe,
  ],
  templateUrl: './country-details.component.html',
  styleUrl: './country-details.component.scss',
})
export class CountryDetailsComponent {
  private translate = inject(TranslateService);

  private databaseSelector: EntityKey = 'country';

  dataId: string | null = null;
  dataSignal = signal<DatabaseManagement | null>(null);

  isLoading = true;

  activeTab: Tab = 'details';
  tabs: ReadonlyArray<TabDef> = [
    { key: 'details', label: `admin.common.tabs.details` },
    {
      key: 'localizations',
      label: `admin.common.tabs.localizations`,
    },
  ];

  databaseManagementFormGroup: FormGroup<DatabaseManagementFormGroup>;
  activeLanguagesForm: FormArray<FormGroup<ActiveLanguagesForm>> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: GenericDataService,
    private masterdataService: MasterdataService,
    private toast: ToastService,
    private fb: FormBuilder,
    private saveSvc: DatabaseSaveService
  ) {
    const localizationTabForm = this.fb.group<{
      [language: string]: FormGroup<MergeLanguagesWithoutType>;
    }>({});
    this.databaseManagementFormGroup = this.fb.group({
      details: this.fb.nonNullable.group({
        code: this.fb.nonNullable.control('', [
          Validators.required,
          Validators.maxLength(10),
        ]),
        description: this.fb.control('', [Validators.required]),
      }),
      localizationsTab: localizationTabForm,
    });
    this.activeLanguagesForm = this.fb.array<FormGroup<ActiveLanguagesForm>>(
      []
    );
  }

  ngOnInit(): void {
    this.dataId = this.route.snapshot.paramMap.get('id');
    if (!this.dataId) return this.goBack();

    this.loadData();
  }

  private loadData(): void {
    this.isLoading = true;

    forkJoin({
      languages: this.service.loadLanguages(),
      database: this.masterdataService.getCountryById(this.dataId!),
    })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: ({ languages, database: data }) => {
          this.dataSignal.set(data);

          const localizationRecord = this.fb.record<FormControl<string>>({});
          Object.entries(data.localized ?? {}).forEach(([key, value]) => {
            localizationRecord.addControl(
              key,
              this.fb.nonNullable.control(String(value ?? ''))
            );
          });

          const localizationTypeRecord = this.fb.record<FormControl<string>>(
            {}
          );
          Object.entries(data.localizedType ?? {}).forEach(([key, value]) => {
            localizationTypeRecord.addControl(
              key,
              this.fb.nonNullable.control(String(value ?? ''))
            );
          });

          this.databaseManagementFormGroup.patchValue({
            details: {
              code: data.code,
              description: data.description,
            },
          });

          this.activeLanguagesForm?.clear();
          languages.forEach((lang) => {
            this.activeLanguagesForm?.push(
              this.fb.group({
                code: this.fb.nonNullable.control(lang.code),

                description: this.fb.nonNullable.control(lang.description),
              })
            );
          });

          const merged = this.mergeLanguages(languages, data.localized ?? {});
          this.buildLanguagesForm(merged);
        },
        error: () => {},
      });
  }

  private mergeLanguages(
    languages: Language[],
    localized: Record<string, string>
  ) {
    return languages.map((lang) => ({
      language: lang,
      localizedName: localized[lang.description] ?? '',
    }));
  }

  private buildLanguagesForm(
    merged: {
      language: Language;
      localizedName: string;
    }[]
  ) {
    const formGroup = this.fb.group<{
      [language: string]: FormGroup<MergeLanguagesWithoutType>;
    }>({});

    for (const item of merged) {
      const isEN = item.language.code === 'EN';
      formGroup.addControl(
        item.language.code,
        this.fb.group({
          localizedName: this.fb.nonNullable.control(
            item.localizedName,
            isEN ? [Validators.required] : []
          ),
        })
      );
    }

    this.databaseManagementFormGroup.setControl('localizationsTab', formGroup);
  }

  saveData(): void {
    if (!this.dataSignal()) return;

    if (this.databaseManagementFormGroup.invalid) {
      this.databaseManagementFormGroup.markAllAsTouched();
      this.databaseManagementFormGroup.updateValueAndValidity({
        emitEvent: true,
      });
      return;
    }

    this.saveSvc.saveFromForms(
      this.databaseManagementFormGroup,
      { entity: this.databaseSelector },
      this.dataSignal()
    );
  }

  deleteData(): void {
    if (!this.dataId) return;
    this.isLoading = true;
    this.masterdataService
      .deleteCountryById(this.dataId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant(
              `admin.${this.databaseSelector}.toasts.delete`
            )
          );
          this.goBack();
        },
        error: () => {
          this.toast.error(
            this.translate.instant(
              `admin.${this.databaseSelector}.toasts.error`
            )
          );
        },
      });
  }

  setTab(tab: Tab): void {
    this.activeTab = tab;
  }

  goBack(): void {
    const target =
      history.state?.from || `/admin/database/${this.databaseSelector}`;
    this.router.navigate([target], { relativeTo: this.route });
  }
}
